import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { usePusher } from '../../contexts/PusherContext';
import { useThreadLoading } from '../../contexts/ThreadLoadingContext';
import {
  AgentEvents,
  WebRTCCallRequestedEvent,
  WebRTCCallAnsweredEvent,
} from '@dreamai/types';
import { ReactComponent as CallingIcon } from '../../icons/webrtc-calls/calling.svg';
import api from '../../api';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import './call-notification.scss';

interface CallNotification {
  threadId: string;
  agentName?: string;
  message?: string;
  timestamp: number;
}

// BroadcastChannel for cross-tab sound coordination
const CALL_SOUND_CHANNEL = 'dream-call-sound';

interface SoundCoordinationMessage {
  type: 'claiming_sound' | 'call_answered';
  threadId: string;
  tabId: string;
}

// Generate unique tab ID
const TAB_ID = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const CallNotification: React.FC = () => {
  const { voiceCallingEnabled } = useFeatureFlags();
  const [activeCall, setActiveCall] = useState<CallNotification | null>(null);
  const [isPlayingSound, setIsPlayingSound] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, getAccessTokenSilently } = useAuth0();
  const userId = user?.sub;
  const pusherContext = usePusher(true);
  const pusher = pusherContext?.pusherInstance;
  const {
    registerWebRTCCallDismissedCallback,
    unregisterWebRTCCallDismissedCallback,
    dismissWebRTCCall,
    registerWebRTCCallAcceptedCallback,
    unregisterWebRTCCallAcceptedCallback,
    setActiveWebRTCCall,
  } = useThreadLoading();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const lastCallRequestRef = useRef<string | null>(null);
  const playPromiseRef = useRef<Promise<void> | null>(null);
  const claimTimestampRef = useRef<number>(0);

  // Initialize audio element and BroadcastChannel
  useEffect(() => {
    // Create audio element for call notification sound
    audioRef.current = new Audio('/call-ring.mp3');
    audioRef.current.loop = true;

    // Create BroadcastChannel for cross-tab sound coordination
    broadcastChannelRef.current = new BroadcastChannel(CALL_SOUND_CHANNEL);
    broadcastChannelRef.current.onmessage = (
      event: MessageEvent<SoundCoordinationMessage>,
    ) => {
      const { type, tabId } = event.data;

      if (type === 'claiming_sound' && tabId !== TAB_ID) {
        // Another tab claimed the sound - only stop ours if we didn't just claim
        // This prevents race conditions where both tabs claim simultaneously
        const timeSinceOurClaim = Date.now() - claimTimestampRef.current;
        if (timeSinceOurClaim > 500) {
          // We didn't recently claim, so yield to the other tab
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
          setIsPlayingSound(false);
        }
        // If we claimed within 500ms, ignore the other tab's claim - we win
      } else if (type === 'call_answered') {
        // Call was answered in another tab - dismiss ALL notifications
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        setIsPlayingSound(false);
        setActiveCall(null);
      }
    };

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }
    };
  }, []);

  const playSound = useCallback((threadId: string) => {
    if (audioRef.current) {
      // Mark our claim timestamp and broadcast to other tabs
      claimTimestampRef.current = Date.now();
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({
          type: 'claiming_sound',
          threadId,
          tabId: TAB_ID,
        } as SoundCoordinationMessage);
      }

      // Reset and play - track the promise to prevent AbortError on rapid play/pause
      audioRef.current.currentTime = 0;
      playPromiseRef.current = audioRef.current.play();
      playPromiseRef.current
        .then(() => {
          setIsPlayingSound(true);
        })
        .catch((error) => {
          // Only log if it's not an AbortError (which is expected when sound is stopped)
          if (error instanceof Error && error.name !== 'AbortError') {
            console.error('Error playing call notification sound:', error);
          }
        });
    }
  }, []);

  const stopSound = useCallback(async () => {
    // Wait for any pending play operation to complete before pausing
    if (playPromiseRef.current) {
      try {
        await playPromiseRef.current;
      } catch {
        // Ignore errors from play attempt
      }
      playPromiseRef.current = null;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlayingSound(false);
  }, []);

  const handleCallRequest = useCallback(
    (data: WebRTCCallRequestedEvent) => {
      // Voice calling disabled: never surface an inbound call. Otherwise the
      // ringing sound would start here while the notification UI (and its
      // dismiss button) is gated off, leaving no way to stop it.
      if (!voiceCallingEnabled) return;

      // Deduplicate events - the same call request may come from multiple channels
      const callKey = `${data.threadId}-${data.agentId || ''}`;
      if (lastCallRequestRef.current === callKey) {
        console.log('Duplicate call notification ignored:', callKey);
        return;
      }
      lastCallRequestRef.current = callKey;

      // Clear the deduplication key after a short delay to allow new calls
      setTimeout(() => {
        if (lastCallRequestRef.current === callKey) {
          lastCallRequestRef.current = null;
        }
      }, 2000);

      console.log('Global call notification received:', data);

      // Set active call notification
      setActiveCall({
        threadId: data.threadId,
        agentName: data.agentName,
        message: data.message,
        timestamp: Date.now(),
      });

      // Also record the call in the shared active-call store. The thread
      // channel normally does this, but for freshly created threads (e.g. a
      // new project's kickoff call) the user may not be subscribed to the
      // thread channel yet — without this, ThreadView would never show the
      // in-thread prompt while this global toast hides itself on-thread.
      setActiveWebRTCCall(data.threadId, {
        agentName: data.agentName,
        message: data.message,
        structuredCallId: data.structuredCallId,
      });

      // Play sound (will coordinate with other tabs)
      playSound(data.threadId);
    },
    [playSound, setActiveWebRTCCall, voiceCallingEnabled],
  );

  useEffect(() => {
    if (!pusher || !userId) return;

    // Subscribe to the user's private channel for global call notifications
    // Replace | with - to match backend channel naming
    const sanitizedUserId = userId.replace('|', '-');
    const channelName = `${process.env.REACT_APP_PUSHER_ENV}-private-user-${sanitizedUserId}`;
    const userChannel = pusher.subscribe(channelName);

    userChannel.bind(
      AgentEvents.WEBRTC_CALL_REQUESTED,
      (data: WebRTCCallRequestedEvent) => {
        handleCallRequest(data);
      },
    );

    // Listen for call answered events (from other tabs/devices via server)
    // Dismiss ALL notifications when any call is answered
    userChannel.bind(
      AgentEvents.WEBRTC_CALL_ANSWERED,
      (data: WebRTCCallAnsweredEvent) => {
        console.log('Call answered event received:', data);
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        setIsPlayingSound(false);
        setActiveCall(null);
      },
    );

    return () => {
      userChannel.unbind(AgentEvents.WEBRTC_CALL_REQUESTED);
      userChannel.unbind(AgentEvents.WEBRTC_CALL_ANSWERED);
      pusher.unsubscribe(channelName);
    };
  }, [pusher, userId, handleCallRequest]);

  // Register callback to listen for call dismissed events from other components
  useEffect(() => {
    const callbackId = 'call-notification-dismissed';

    registerWebRTCCallDismissedCallback(callbackId, (threadId: string) => {
      // If the dismissed call matches our active call, stop sound and clear notification
      if (activeCall && activeCall.threadId === threadId) {
        console.log('Call dismissed from another component, cleaning up');
        stopSound();
        setActiveCall(null);
      }
    });

    return () => {
      unregisterWebRTCCallDismissedCallback(callbackId);
    };
  }, [
    activeCall,
    stopSound,
    registerWebRTCCallDismissedCallback,
    unregisterWebRTCCallDismissedCallback,
  ]);

  // Register callback to listen for call accepted events from other components
  useEffect(() => {
    const callbackId = 'call-notification-accepted';

    registerWebRTCCallAcceptedCallback(callbackId, (threadId: string) => {
      // If the accepted call matches our active call, stop sound and clear notification
      if (activeCall && activeCall.threadId === threadId) {
        console.log('Call accepted from another component, cleaning up');
        stopSound();
        setActiveCall(null);
      }
    });

    return () => {
      unregisterWebRTCCallAcceptedCallback(callbackId);
    };
  }, [
    activeCall,
    stopSound,
    registerWebRTCCallAcceptedCallback,
    unregisterWebRTCCallAcceptedCallback,
  ]);

  const handleAccept = useCallback(() => {
    if (!activeCall) return;

    stopSound();
    setIsPlayingSound(false);

    // Broadcast to other tabs that call was answered
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({
        type: 'call_answered',
        threadId: activeCall.threadId,
        tabId: TAB_ID,
      } as SoundCoordinationMessage);
    }

    setActiveCall(null);

    // Navigate to the thread with the call
    navigate(`/conversations?thread_id=${activeCall.threadId}`);
  }, [activeCall, navigate, stopSound]);

  const handleDismiss = useCallback(async () => {
    if (!activeCall) return;

    stopSound();
    setActiveCall(null);

    // Notify other components that the call was dismissed
    dismissWebRTCCall(activeCall.threadId);

    // Notify backend that user declined the call
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/webrtc/decline-call',
        {
          thread_id: activeCall.threadId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
    } catch (error) {
      console.error('Error declining call:', error);
      // Silent fail - user already dismissed the notification
    }
  }, [activeCall, stopSound, getAccessTokenSilently, dismissWebRTCCall]);

  // Auto-dismiss after 60 seconds
  useEffect(() => {
    if (!activeCall) return;

    const timer = setTimeout(() => {
      handleDismiss();
    }, 60000); // 60 seconds

    return () => clearTimeout(timer);
  }, [activeCall, handleDismiss]);

  if (!voiceCallingEnabled) return null;
  if (!activeCall) return null;

  // Check if we're currently on the thread that's calling
  const currentThreadId = searchParams.get('thread_id');
  const isOnActiveThread = currentThreadId === activeCall.threadId;

  // If we're on the active thread, don't show the notification (but sound still plays)
  if (isOnActiveThread) return null;

  return (
    <div className='call-notification-wrapper'>
      <div className='call-notification'>
        <div className='call-notification-icon'>
          <CallingIcon />
        </div>
        <div className='call-notification-content'>
          <div className='call-notification-title'>
            {activeCall.agentName || 'Agent'} is calling
          </div>
          {activeCall.message && (
            <div className='call-notification-message'>
              {activeCall.message}
            </div>
          )}
        </div>
        <div className='call-notification-actions'>
          <button className='call-accept-button' onClick={handleAccept}>
            Answer
          </button>
          <button className='call-dismiss-button' onClick={handleDismiss}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallNotification;
